/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/11/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Eliminaciones de preventa en otras acciones
*      3       | 05/06/2026 | Lic. José Luis Aragón Cervantes  | Renombre de recuperación local a edición
* ---------------------------------------------------------------------------------------
*/

import {
  Box,
  IconButton,
  Tooltip,
  ClickAwayListener,
  Typography,
  Stack,
  ButtonBase,
  Divider
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import NextPlanIcon from "@mui/icons-material/NextPlan";
import DeleteIcon from "@mui/icons-material/Delete";

import { useVentas } from "../../context/Ventas/VentasContext";
import { useDoubleClickPrevention } from "../../hooks/useDoubleClickPrevention";

export default function MnuOtherActionsPreventas() {
  const {
    openModalPreventa,
    handleCerrarMenuModalPreventa,
    handleAbrirModalRecuperarPreVentaFirma,
    handleClickEliminarPreVenta,
    handleClickEliminarPartidaPreVentas,
    partidaSeleccionadaPreventas,
    setVentaTotales,
    getVentaTotalesForm,
    setClienteForm,
  } = useVentas();
  const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

  if (!openModalPreventa) return null;

  const handleCloseMenu = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();
    handleCerrarMenuModalPreventa();
  };

  const handleClickAway = (event) => {
    const target = event?.target;
    if (target?.closest?.("[data-preventa-menu-root='true']")) {
      return;
    }

    handleCerrarMenuModalPreventa();
  };

  const handleOpenEditarPreventa = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();

    // Abrimos la acción primero.
    handleAbrirModalRecuperarPreVentaFirma();

    // Cerramos el menú al siguiente ciclo para evitar que el ClickAway
    // o el re-focus del lector maten el click actual.
    window.setTimeout(() => {
      handleCerrarMenuModalPreventa();
    }, 0);
  };

  const handleEliminarPartida = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    executeWithProtection(() => handleClickEliminarPartidaPreVentas(partidaSeleccionadaPreventas?.idpartida));
    handleCerrarMenuModalPreventa();
  };

  const handleEliminarPreventa = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    executeWithProtection(() => {
      handleClickEliminarPreVenta();
      setVentaTotales(getVentaTotalesForm());
      setClienteForm({});
    });
    handleCerrarMenuModalPreventa();
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        data-preventa-menu-root="true"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: 320,
          maxWidth: "90vw",
          height: "100vh",
          bgcolor: "#191970",
          color: "white",
          boxShadow: 10,
          p: 2,
          display: "flex",
          flexDirection: "column",
          zIndex: 2000,
          transform: "translateX(0)",
          transition: "transform .25s ease-in-out",
          overflowY: "auto",
          userSelect: "none"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              letterSpacing: ".3px"
            }}
          >
            Otras acciones
          </Typography>

          <IconButton
            onClick={handleCloseMenu}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              color: "white",
              "&:hover": {
                color: "#ff4136",
                bgcolor: "rgba(255,255,255,.08)"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,.18)", mb: 1.5 }} />

        <Stack spacing={1} alignItems="flex-start">
          <Tooltip title="Editar preventa" placement="left">
            <ButtonBase
              onClick={handleOpenEditarPreventa}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.25,
                p: 1.25,
                borderRadius: 1.5,
                color: "white",
                textAlign: "left",
                transition: "all .18s ease",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,.10)"
                },
                "&:focus-visible": {
                  outline: "2px solid rgba(255,255,255,.45)",
                  outlineOffset: "2px"
                }
              }}
            >
              <NextPlanIcon sx={{ fontSize: 24, flexShrink: 0 }} />

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.2
                  }}
                >
Editar preventa {`{Alt+J}`}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,.72)",
                    mt: 0.25
                  }}
                >
                  {`{Alt+J}`}
                </Typography>
              </Box>
            </ButtonBase>
          </Tooltip>

          <Tooltip title="Eliminar partida" placement="left">
            <ButtonBase
              onClick={handleEliminarPartida}
              disabled={isProcessing || !partidaSeleccionadaPreventas?.idpartida}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.25,
                p: 1.25,
                borderRadius: 1.5,
                color: "white",
                textAlign: "left",
                transition: "all .18s ease",
                opacity: isProcessing || !partidaSeleccionadaPreventas?.idpartida ? 0.35 : 1,
                "&:hover": {
                  bgcolor: "rgba(255,255,255,.10)"
                },
                "&:focus-visible": {
                  outline: "2px solid rgba(255,255,255,.45)",
                  outlineOffset: "2px"
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: 24, flexShrink: 0, color: "#ffb3b3" }} />

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Eliminar partida {`{Alt+F7}`}
                </Typography>
              </Box>
            </ButtonBase>
          </Tooltip>

          <Tooltip title="Eliminar preventa" placement="left">
            <ButtonBase
              onClick={handleEliminarPreventa}
              disabled={isProcessing}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.25,
                p: 1.25,
                borderRadius: 1.5,
                color: "white",
                textAlign: "left",
                transition: "all .18s ease",
                opacity: isProcessing ? 0.35 : 1,
                "&:hover": {
                  bgcolor: "rgba(255,255,255,.10)"
                },
                "&:focus-visible": {
                  outline: "2px solid rgba(255,255,255,.45)",
                  outlineOffset: "2px"
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: 24, flexShrink: 0, color: "#ffb3b3" }} />

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Eliminar preventa {`{Alt+F12}`}
                </Typography>
              </Box>
            </ButtonBase>
          </Tooltip>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
}
